import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requestMagicLink } from "../api/client";
import { BRAND } from "../brand";

const HERO_IMAGES = [
  "/images/heroes/mm/hero-1.webp",
  "/images/heroes/mm/hero-2.webp",
  "/images/heroes/mm/hero-3.webp",
  "/images/heroes/mm/hero-4.webp",
  "/images/heroes/mm/hero-5.webp",
  "/images/heroes/mm/hero-6.webp",
  "/images/heroes/mm/hero-7.webp",
];

function randomHeroImage(): string {
  return HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)] as string;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [heroImage] = useState(randomHeroImage);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestMagicLink(email.trim());
      navigate("/auth/check-email");
    } catch {
      setError("We couldn't send that link. Please check the address and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-[70vh] items-center justify-center overflow-hidden rounded-xl">
      <div className="absolute inset-0 hidden lg:block">
        <img src={heroImage} alt={`${BRAND.name} training`} className="h-full w-full object-cover" data-testid="auth-hero-image" />
        <div className="absolute inset-0 bg-black/40" />
      </div>
      <div className="relative flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-8 rounded-xl border border-white/10 bg-gray-900/70 p-8 shadow-2xl backdrop-blur-lg dark:bg-gray-900/80">
          <div className="text-center">
            <div className="mb-6 flex items-center justify-center gap-2.5">
              <img src={BRAND.logoPath} alt={BRAND.name} className="h-9 w-9" />
              <span className="text-xl font-semibold tracking-tight text-white">{BRAND.name}</span>
            </div>
            <h1 className="text-2xl font-semibold text-white" data-testid="login-page-title">Welcome back</h1>
            <p className="mt-1.5 text-sm text-gray-300">Sign in to your running journal.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form-submit">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-200">Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="login-email-input"
              />
            </div>
            {error && (
              <p className="text-sm text-amber-400" role="alert" data-testid="login-error-message">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition-all hover:bg-emerald-700 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {busy ? "Sending…" : "Send Magic Link"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-300">
            Don't have an account?{" "}
            <Link to="/register" className="font-medium text-emerald-400 hover:text-emerald-300" data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
